import pickBy from 'lodash/pickBy'
import some from 'lodash/some'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoRectangularManway,
  GeckoRectangularManwayJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  ContainmentOptions,
  regenerateVerticalTankShellComponent,
} from 'types/assetModel/assetModelHelpers'
import {
  compassOrientationFormField,
  installationDateFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoCoatingRegion,
  GeckoCoatingRegionJSON,
} from 'types/assetModel/features/geckoCoatingRegion'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoLinerRegion,
  GeckoLinerRegionJSON,
} from 'types/assetModel/features/geckoLinerRegion'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
} from 'types/assetModel/features/geckoPolygonalPlate'
import {
  GeckoCourseJSON,
  GeckoShellCourse,
} from 'types/assetModel/features/verticalTankShell/geckoShellCourse'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { getDropdownOptionsForEnum } from 'utils/enum'
import { forecastMath } from 'utils/fcMath'
import { mergeMaps } from 'utils/mergeMaps'
import { parseDate, parseEnum, parseNumber } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath
import { v4 as uuid4 } from 'uuid'

export type VerticalTankShellAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.VerticalTankShell
  height: number
  radius: number
  material: string | undefined
  installation_date: string | undefined
  joint_efficiency: number | undefined
  stress_tolerance_upper_shell_courses: number | undefined
  stress_tolerance_lower_shell_courses: number | undefined
  containment: string | undefined
  features: Record<
    string,
    | GeckoCourseJSON
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
    | GeckoRectangularManwayJSON
    | GeckoFixedSensorJSON
    | GeckoPolygonalPlateJSON
    | GeckoCoatingRegionJSON
    | GeckoLinerRegionJSON
  >
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  compass_orientation: number | undefined
}

export const VerticalTankShellAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Course,
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
  GeckoFeatureModelType.RectangularManway,
  GeckoFeatureModelType.FixedSensor,
  GeckoFeatureModelType.PolygonalPlate,
  GeckoFeatureModelType.CoatingRegion,
  GeckoFeatureModelType.LinerRegion,
]

export const NewVerticalTankShellComponentJSON = {
  name: 'New Vertical Tank Shell',
  type: GeckoComponentModelType.VerticalTankShell,
  height: 0,
  radius: 0,
  material: undefined,
  installation_date: undefined,
  joint_efficiency: undefined,
  stress_tolerance_upper_shell_courses: undefined,
  stress_tolerance_lower_shell_courses: undefined,
  containment: undefined,
  features: {},
  scene_config: {},
  geometry_scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  compass_orientation: undefined,
} as VerticalTankShellAssetModelJSON

export class GeckoVerticalTankShell extends GeckoEditableComponent {
  type: GeckoComponentModelType.VerticalTankShell
  radius: GeckoMeasurement
  height: GeckoMeasurement
  material: string | undefined
  installationDate: Date | undefined
  jointEfficiency: number | undefined
  stressToleranceUpperShellCourses: GeckoMeasurement | undefined
  stressToleranceLowerShellCourses: GeckoMeasurement | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  containment: ContainmentOptions | undefined
  courses: Map<string, GeckoShellCourse>
  plates: Map<string, GeckoPlate>
  polygonalPlates: Map<string, GeckoPolygonalPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  rectangularManways: Map<string, GeckoRectangularManway>
  fixedSensors: Map<string, GeckoFixedSensor>
  coatingRegions: Map<string, GeckoCoatingRegion>
  linerRegions: Map<string, GeckoLinerRegion>
  features: Map<
    string,
    | GeckoShellCourse
    | GeckoPlate
    | GeckoPolygonalPlate
    | GeckoHorizontalWeld
    | GeckoVerticalWeld
    | GeckoNozzle
    | GeckoManway
    | GeckoRectangularManway
    | GeckoFixedSensor
    | GeckoCoatingRegion
    | GeckoLinerRegion
  >
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    height: GeckoMeasurement,
    material: string | undefined,
    installationDate: Date | undefined,
    jointEfficiency: number | undefined,
    stressToleranceUpperShellCourses: GeckoMeasurement | undefined,
    stressToleranceLowerShellCourses: GeckoMeasurement | undefined,
    componentBounds: GeckoComponentRect,
    containment: ContainmentOptions | undefined,
    sceneConfig: SceneConfig,
    courses: Map<string, GeckoShellCourse>,
    plates: Map<string, GeckoPlate>,
    polygonalPlates: Map<string, GeckoPolygonalPlate>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    rectangularManways: Map<string, GeckoRectangularManway>,
    coatingRegions: Map<string, GeckoCoatingRegion>,
    linerRegions: Map<string, GeckoLinerRegion>,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.VerticalTankShell
    this.radius = radius
    this.height = height
    this.material = material
    this.installationDate = installationDate
    this.jointEfficiency = jointEfficiency
    this.stressToleranceUpperShellCourses = stressToleranceUpperShellCourses
    this.stressToleranceLowerShellCourses = stressToleranceLowerShellCourses
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.compassOrientation = compassOrientation
    this.containment = containment

    // Features
    this.courses = courses
    this.plates = plates
    this.polygonalPlates = polygonalPlates
    this.horizontalWelds = horizontalWelds
    this.verticalWelds = verticalWelds
    this.nozzles = nozzles
    this.manways = manways
    this.rectangularManways = rectangularManways
    this.fixedSensors = fixedSensors
    this.coatingRegions = coatingRegions
    this.linerRegions = linerRegions

    this.features = mergeMaps(
      courses,
      plates,
      polygonalPlates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways,
      rectangularManways,
      fixedSensors,
      coatingRegions,
      linerRegions
    )
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get polygonalPlatesArray() {
    return Array.from(this.polygonalPlates.values())
  }

  public get coursesArray() {
    return Array.from(this.courses.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public get rectangularManwaysArray() {
    return Array.from(this.rectangularManways.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get coatingRegionsArray() {
    return Array.from(this.coatingRegions.values())
  }

  public get linerRegionsArray() {
    return Array.from(this.linerRegions.values())
  }

  public static CreateFromJSON(
    id: string,
    json: VerticalTankShellAssetModelJSON
  ) {
    const name = json.name
    const shellRadius = GeckoMeasurement.CreateFromValue(
      json.radius,
      GeckoMeasurementUnit.Meters
    )
    const shellHeight = GeckoMeasurement.CreateFromValue(
      json.height,
      GeckoMeasurementUnit.Meters
    )
    const material = json.material
    const installationDate = parseDate(json.installation_date)
    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressToleranceUpperShellCourses = parseNumber(
      json.stress_tolerance_upper_shell_courses
    )

    const stressToleranceUpperShellCourses =
      parsedStressToleranceUpperShellCourses !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressToleranceUpperShellCourses,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined

    const parsedStressToleranceLowerShellCourses = parseNumber(
      json.stress_tolerance_lower_shell_courses
    )
    const stressToleranceLowerShellCourses =
      parsedStressToleranceLowerShellCourses !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressToleranceLowerShellCourses,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData =
      json.features as VerticalTankShellAssetModelJSON['features']

    const childCourseJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Course
    })
    const courses = GeckoShellCourse.CreateMapFromJSON(
      childCourseJSON as Record<string, GeckoCourseJSON>,
      componentBounds,
      GeckoMeasurementUnit.Meters
    )

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childCoatingRegionJSON = pickBy(
      featureData,
      (child) => child.type === GeckoFeatureModelType.CoatingRegion
    )
    const coatingRegions = GeckoCoatingRegion.CreateMapFromJSON(
      id,
      childCoatingRegionJSON as Record<string, GeckoCoatingRegionJSON>
    )

    const childLinerRegionJSON = pickBy(
      featureData,
      (child) => child.type === GeckoFeatureModelType.LinerRegion
    )
    const linerRegion = GeckoLinerRegion.CreateMapFromJSON(
      id,
      childLinerRegionJSON as Record<string, GeckoLinerRegionJSON>
    )

    const childPolygonalPlateJSON = pickBy(
      featureData,
      (child) => child.type === GeckoFeatureModelType.PolygonalPlate
    )
    const polygonalPlates = GeckoPolygonalPlate.CreateMapFromJSON(
      id,
      childPolygonalPlateJSON as Record<string, GeckoPolygonalPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    })
    const manways = GeckoManway.CreateMapFromJSON(
      id,
      childManwayJSON as Record<string, GeckoManwayJSON>
    )

    const childRectangularManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.RectangularManway
    })
    const rectangularManways = GeckoRectangularManway.CreateMapFromJSON(
      id,
      childRectangularManwayJSON as Record<string, GeckoRectangularManwayJSON>
    )

    const childFixedSensorJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )
    const compassOrientation = parseNumber(json.compass_orientation)

    const containment = parseEnum(ContainmentOptions, json.containment)

    return new GeckoVerticalTankShell(
      id,
      json,
      name,
      shellRadius,
      shellHeight,
      material,
      installationDate,
      jointEfficiency,
      stressToleranceUpperShellCourses,
      stressToleranceLowerShellCourses,
      componentBounds,
      containment,
      sceneConfig,
      courses,
      plates,
      polygonalPlates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways,
      fixedSensors,
      rectangularManways,
      coatingRegions,
      linerRegion,
      compassOrientation
    )
  }

  public asJSON(): VerticalTankShellAssetModelJSON {
    return generateVerticalTankShell(
      this.name,
      this.height,
      this.radius,
      this.json as VerticalTankShellAssetModelJSON,
      this.material,
      this.installationDate,
      this.jointEfficiency,
      this.stressToleranceUpperShellCourses,
      this.stressToleranceLowerShellCourses,
      this.compassOrientation,
      this.containment
    )
  }
}

const generateVerticalTankShell = (
  name: string,
  height: GeckoMeasurement,
  radius: GeckoMeasurement,
  otherVerticalTankShellData: VerticalTankShellAssetModelJSON = NewVerticalTankShellComponentJSON,
  material: string | undefined,
  installationDate: Date | undefined,
  jointEfficiency: number | undefined,
  stressToleranceUpperShellCourses: GeckoMeasurement | undefined,
  stressToleranceLowerShellCourses: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  containment: string | undefined
): VerticalTankShellAssetModelJSON => {
  const tankInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined
  const tankStressToleranceUpperShellCourses =
    stressToleranceUpperShellCourses !== undefined
      ? stressToleranceUpperShellCourses
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined
  const tankStressToleranceLowerShellCourses =
    stressToleranceLowerShellCourses !== undefined
      ? stressToleranceLowerShellCourses
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined
  const tankHeight = height
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tankRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const tankCircumference = Number(
    (2 * pi * tankRadius).toPrecision(assetModelPrecision)
  )

  // ensure all tank shells have at least one course (used for nominal inputs)
  const hasAShellCourse = some(
    Object.values(otherVerticalTankShellData.features),
    (feature) => feature.type === GeckoFeatureModelType.Course
  )

  const featureSetToUse: VerticalTankShellAssetModelJSON['features'] = {
    ...otherVerticalTankShellData.features,
    ...(hasAShellCourse
      ? {}
      : {
          [`course-${uuid4().slice(0, 6)}`]: {
            type: GeckoFeatureModelType.Course,
            name: 'Course 1',
            feature_geometry: {
              y_end: tankHeight,
            },
            nominal: {
              wall_thickness: undefined,
              coating_thickness: undefined,
            },
            tmin: undefined,
            stress_tolerance_override: undefined,
          },
        }),
  }

  return regenerateVerticalTankShellComponent({
    name: name,
    type: GeckoComponentModelType.VerticalTankShell,
    material: material,
    installation_date: tankInstallationDate,
    joint_efficiency: jointEfficiency,
    stress_tolerance_upper_shell_courses: tankStressToleranceUpperShellCourses,
    stress_tolerance_lower_shell_courses: tankStressToleranceLowerShellCourses,
    containment: containment,
    height: tankHeight,
    radius: tankRadius,
    features: featureSetToUse,
    scene_config: otherVerticalTankShellData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: tankCircumference,
      y_end: tankHeight,
    },
    compass_orientation: compassOrientation,
  }) as VerticalTankShellAssetModelJSON
}

export const VerticalTankShellAssetModelFormFields = (
  verticalTankShellJSON: VerticalTankShellAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Shell Name',
      value: verticalTankShellJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: verticalTankShellJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: verticalTankShellJSON.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: verticalTankShellJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(verticalTankShellJSON.installation_date, true),
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: verticalTankShellJSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'containment',
      label: 'Containment',
      value: verticalTankShellJSON.containment,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: getDropdownOptionsForEnum(ContainmentOptions),
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    compassOrientationFormField(verticalTankShellJSON.compass_orientation),
  ]
}

export const VerticalTankShellJSONFromFormFields = (
  component: VerticalTankShellAssetModelJSON,
  formFields: FormField[]
) => {
  //Remove these fields over time - keeping them in the type for now for backwards compatibility but long term stress tolerances should be on the course level
  const stressToleranceLowerShellCourses = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance_lower_shell_courses')
      ?.value
  )
  const stressToleranceUpperShellCourses = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance_upper_shell_courses')
      ?.value
  )
  const material = getFormFieldByDataKey(formFields, 'material')
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getFormFieldByDataKey(
    formFields,
    'compass_orientation'
  )

  const containment = getFormFieldByDataKey(formFields, 'containment')

  return generateVerticalTankShell(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2,
      GeckoMeasurementUnit.Meters
    ),
    component,
    material?.value ? (material?.value as string) : undefined,
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parseNumber(getFormFieldByDataKey(formFields, 'joint_efficiency')?.value),
    stressToleranceUpperShellCourses !== undefined
      ? GeckoMeasurement.CreateFromValue(
          stressToleranceUpperShellCourses,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    stressToleranceLowerShellCourses !== undefined
      ? GeckoMeasurement.CreateFromValue(
          stressToleranceLowerShellCourses,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    parseNumber(compassOrientation?.value),
    parseEnum(ContainmentOptions, containment?.value)
  )
}
